import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Dimensions } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useSobriety } from '@/contexts/SobrietyContext';

const SCREEN_WIDTH = Dimensions.get('window').width;
const MEDIUM_WIDGET_WIDTH = Math.min(349, SCREEN_WIDTH - 48);
const MEDIUM_WIDGET_HEIGHT = 164;
const SMALL_WIDGET_SIZE = 164;

function ClockIcon() {
  return (
    <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
      <Path
        d="M4.46484 14.5857L5.89917 13.1505M15.0714 14.5857L13.6372 13.1505M5.10834 4.62256L5.89917 5.41336"
        stroke="#669AB7"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M9.76879 3.21631V3.81446M9.76879 16.7858V14.7571M17.2688 9.28581H15.2402M2.73438 9.28581H4.29746"
        stroke="#669AB7"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function ProgressBar({ progress }: { progress: number }) {
  return (
    <View style={styles.progressTrack}>
      <View style={[styles.progressFill, { width: `${progress * 100}%` }]} />
    </View>
  );
}

function useWidgetData() {
  const { getCurrentDays, sobrietyState } = useSobriety();
  const totalDays = getCurrentDays();
  const isSetup = sobrietyState.isSetup;

  const progress = useMemo(() => {
    const now = new Date();
    const minutesElapsed = now.getHours() * 60 + now.getMinutes();
    return Math.min(Math.max(minutesElapsed / (24 * 60), 0), 1);
  }, []);

  return { totalDays, isSetup, progress };
}

export function SmallWidgetPreview() {
  const { totalDays, isSetup, progress } = useWidgetData();

  return (
    <View style={styles.smallContainer}>
      <View style={styles.topSection}>
        <View style={styles.leftGroup}>
          <ClockIcon />
          <Text style={styles.hojeText}>Hoje</Text>
        </View>
        <ProgressBar progress={progress} />
      </View>

      <View style={styles.smallBottomSection}>
        {isSetup ? (
          <Text style={styles.sobrietyText}>
            {totalDays} dias em sobriedade
          </Text>
        ) : (
          <Text style={styles.sobrietyText}>
            Configure seu{'\n'}clock de sobriedade
          </Text>
        )}
      </View>
    </View>
  );
}

export default function WidgetPreview() {
  const { totalDays, isSetup, progress } = useWidgetData();

  return (
    <View style={styles.container}>
      <View style={styles.topSection}>
        <View style={styles.headerRow}>
          <View style={styles.leftGroup}>
            <ClockIcon />
            <Text style={styles.hojeText}>Hoje</Text>
          </View>
          <Text style={styles.horasText}>+ 24 horas</Text>
        </View>
        <ProgressBar progress={progress} />
      </View>

      <View style={styles.bottomSection}>
        {isSetup ? (
          <View style={styles.sobrietyTextFlex}>
            <Text style={styles.sobrietyText}>
              Você já está a <Text style={styles.sobrietyTextBold}>{totalDays} dias</Text>
            </Text>
            <Text style={styles.sobrietyText}>em sobriedade</Text>
          </View>
        ) : (
          <View style={styles.sobrietyTextFlex}>
            <Text style={styles.sobrietyText}>Configure seu</Text>
            <Text style={styles.sobrietyText}>clock de sobriedade</Text>
          </View>
        )}

        <View style={styles.shareButton}>
          <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
            <Path
              d="M8.89173 6.73389L6.59375 9.03187"
              stroke="#151515"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.59544 9.03184L2.0521 6.25384C1.45677 5.88984 1.57744 4.99184 2.2481 4.79784L12.9748 1.69917C13.5848 1.5225 14.1488 2.09184 13.9661 2.69984L10.7834 13.3425C10.5841 14.0092 9.6901 14.1238 9.32877 13.5292L6.59544 9.03184Z"
              stroke="#151515"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: MEDIUM_WIDGET_WIDTH,
    height: MEDIUM_WIDGET_HEIGHT,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 24,
    backgroundColor: '#F9F8F3',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    overflow: 'hidden',
  },
  smallContainer: {
    width: SMALL_WIDGET_SIZE,
    height: SMALL_WIDGET_SIZE,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 24,
    backgroundColor: '#F9F8F3',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    overflow: 'hidden',
  },
  topSection: {
    width: '100%',
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  leftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  hojeText: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14 * 1.4,
  },
  horasText: {
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 14 * 1.4,
  },
  progressTrack: {
    width: '100%',
    height: 4,
    backgroundColor: '#F0EEE4',
    borderRadius: 2,
  },
  progressFill: {
    height: 4,
    backgroundColor: '#669AB7',
    borderRadius: 2,
  },
  bottomSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: 24,
    width: '100%',
  },
  smallBottomSection: {
    width: '100%',
  },
  sobrietyText: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 18,
    lineHeight: 25,
    flexShrink: 1,
  },
  sobrietyTextFlex: {
    flex: 1,
  },
  sobrietyTextBold: {
    fontWeight: '700',
  },
  shareButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
